import React, { useState, useEffect } from 'react';

/**
 * 🎯 DEV TOOL: Toggle Iframe Mode
 * 
 * This component allows you to force the app into iframe mode
 * without actually being in an iframe. Useful for development.
 * 
 * To use:
 * 1. Click the toggle button to enable/disable iframe mode
 * 2. The app will automatically switch to horizontal menu (iframe UI)
 * 3. Refresh the page if needed to see full effect
 */
export function IframeDevToggle() {
  const [isIframeMode, setIsIframeMode] = useState(
    localStorage.getItem('force_iframe_mode') === 'true'
  );

  useEffect(() => {
    // Listen for changes to localStorage (in case changed in another tab/console)
    const handleStorageChange = () => {
      setIsIframeMode(localStorage.getItem('force_iframe_mode') === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const toggleIframeMode = () => {
    const newValue = !isIframeMode;
    
    if (newValue) {
      localStorage.setItem('force_iframe_mode', 'true');
      console.log('🎯 [DEV] Iframe Mode: ENABLED');
    } else {
      localStorage.removeItem('force_iframe_mode');
      console.log('🎯 [DEV] Iframe Mode: DISABLED');
    }
    
    setIsIframeMode(newValue);
    
    // Reload page to apply changes
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  // Only show in development
  if (import.meta.env.MODE === 'production') {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 right-4 z-[9999] bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-2xl border-2 border-white/20"
      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-2">
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
              />
            </svg>
            <span className="font-bold text-sm">DEV: Iframe Mode</span>
          </div>
          
          <button
            onClick={toggleIframeMode}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-600 ${
              isIframeMode ? 'bg-green-500' : 'bg-gray-400'
            }`}
            role="switch"
            aria-checked={isIframeMode}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isIframeMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        <div className="text-xs opacity-90">
          {isIframeMode ? (
            <span className="flex items-center gap-1">
              ✅ <strong>Iframe UI Active</strong> (Horizontal menu)
            </span>
          ) : (
            <span className="flex items-center gap-1">
              ⚪ Normal UI (Vertical sidebar)
            </span>
          )}
        </div>
        
        {isIframeMode && (
          <div className="mt-2 text-xs bg-white/10 rounded p-2">
            💡 The app is now in iframe mode. You'll see:
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Horizontal menu (top)</li>
              <li>Compact header</li>
              <li>No theme toggle</li>
              <li>No logout button</li>
            </ul>
          </div>
        )}
      </div>
      
      {/* Quick console commands */}
      <div className="border-t border-white/20 px-4 py-2 text-xs opacity-75">
        <details>
          <summary className="cursor-pointer hover:opacity-100 font-semibold">
            📝 Console Commands
          </summary>
          <div className="mt-2 space-y-1 font-mono text-[10px] bg-black/30 p-2 rounded">
            <div>Enable: <code className="text-green-300">localStorage.setItem('force_iframe_mode', 'true')</code></div>
            <div>Disable: <code className="text-red-300">localStorage.removeItem('force_iframe_mode')</code></div>
            <div>Check: <code className="text-blue-300">localStorage.getItem('force_iframe_mode')</code></div>
          </div>
        </details>
      </div>
    </div>
  );
}

